import { useState } from "react";
import { Users, Plus } from "lucide-react";
import { useEmployees, EMPLOYEE_STATUS_LABEL, EmployeeStatus } from "@/api/employees";
import { useAuthStore } from "@/stores/authStore";
import CreateEmployeeModal from "@/pages/employees/CreateEmployeeModal";

const STATUS_STYLE: Record<EmployeeStatus, string> = {
  ACTIVO: "bg-green-100 text-green-700",
  INACTIVO: "bg-gray-100 text-gray-500",
  TERMINADO: "bg-red-100 text-red-700",
};

export default function EmployeesSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useEmployees({ clientId, pageSize: 50 });
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Empleados</h2>
        {hasPermission("payroll.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nuevo empleado
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando empleados...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene empleados registrados (nómina tercerizada).</p>
        )}
        {data?.data.map((employee) => (
          <div key={employee.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Users size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{employee.fullName}</p>
                <p className="text-xs text-gray-500">{employee.position || "Sin cargo asignado"}</p>
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[employee.status]}`}>
              {EMPLOYEE_STATUS_LABEL[employee.status]}
            </span>
          </div>
        ))}
      </div>

      {showCreate && <CreateEmployeeModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
